import { CommonModule } from '@angular/common'
import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Kanban } from '../../../../_services/kanban/kanban.model'
import { KanbanService } from '../../kanban.service'
import dayjs from 'dayjs'
import { LoadingComponent } from '@visio-ai/ui-kit'

type CreateCardDeltaType = { field: ''; oldValue: null; newValue: null }
type FieldDeltaDisplay = { field: 'fields'; oldValue: Kanban.Card.History.FieldDelta['oldValue']; newValue: Kanban.Card.History.FieldDelta['newValue'] }
type HistoryDeltaDisplay = (Kanban.Card.History.Delta | CreateCardDeltaType | FieldDeltaDisplay) & { label: string }
type HistoryEntryDisplay = Omit<Kanban.Card.History.Entry, 'deltas'> & { deltas: HistoryDeltaDisplay[] }

@Component({
  selector: 'app-card-history',
  imports: [CommonModule, LoadingComponent],
  templateUrl: './card-history.component.html',
  styleUrl: './card-history.component.scss'
})
export class CardHistoryComponent implements OnInit {
  private kanbanService = inject(KanbanService)
  private destroyRef = inject(DestroyRef)

  card = input.required<Kanban.Card.Entity>()
  histories = signal<HistoryEntryDisplay[]>([])

  ngOnInit(): void {
    this.kanbanService.getCardHistory(this.card()?.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (histories) => {
        const sortedHistories = histories.sort((a, b) => b.revision - a.revision)
        // console.log(sortedHistories)
        const parsedHistories: HistoryEntryDisplay[] = sortedHistories.map(history => {
          if (history.revision === 1) {
            return {
              ...history,
              versionCreatedAt: dayjs(history.versionCreatedAt).format('DD/MM/YYYY HH:mm:ss'),
              deltas: [
                {
                  field: '',
                  oldValue: null,
                  newValue: null,
                  label: 'criou o card'
                }
              ]
            }
          }

          return {
            ...history,
            versionCreatedAt: dayjs(history.versionCreatedAt).format('DD/MM/YYYY HH:mm:ss'),
            deltas: history.deltas.filter(delta => delta.newValue !== null || delta.oldValue !== null).flatMap(delta => {
              if (delta.field === 'fields') {
                return this.expandFieldsDelta(delta.newValue as Kanban.Card.History.FieldDelta[])
              }
              return [{ ...delta, label: this.createLabelForDelta(delta) }]
            })
          }
        })
        this.histories.set(parsedHistories)
      }
    })
  }

  private createLabelForDelta(delta: Kanban.Card.History.Delta) {
    switch (delta.field) {
      case 'phaseId': {
        const oldPhaseName = this.kanbanService.getPhaseName(delta.oldValue as string)
        const newPhaseName = this.kanbanService.getPhaseName(delta.newValue as string)
        return `moveu de ${oldPhaseName} para ${newPhaseName}`
      }
      case 'assignedTo': {
        const oldUser = delta.oldValue as Kanban.Card.User | null | undefined
        const newUser = delta.newValue as Kanban.Card.User | null | undefined
        const oldName = oldUser?.name ?? oldUser?.id ?? 'ninguém'
        const newName = newUser?.name ?? newUser?.id ?? 'ninguém'
        return `alterou responsável de "${oldName}" para "${newName}"`
      }
      case 'tags': {
        const board = this.kanbanService.board()
        const removedTags = (delta.oldValue as Kanban.Card.History.CardTagDelta[])
          .map(t => board?.tags.find(bt => bt.id === t.tagId)?.label ?? t.tagId)
        const addedTags = (delta.newValue as Kanban.Card.History.CardTagDelta[])
          .map(t => board?.tags.find(bt => bt.id === t.tagId)?.label ?? t.tagId)
        const parts: string[] = []
        if (addedTags.length) parts.push(`adicionou ${addedTags.map(t => `"${t}"`).join(', ')}`)
        if (removedTags.length) parts.push(`removeu ${removedTags.map(t => `"${t}"`).join(', ')}`)
        return `${parts.join(' e ')} nas tags`
      }
      default:
        return `alterou ${delta.field} de "${delta.oldValue}" para "${delta.newValue}"`
    }
  }

  private expandFieldsDelta(fieldDeltas: Kanban.Card.History.FieldDelta[]): HistoryDeltaDisplay[] {
    const board = this.kanbanService.board()
    return fieldDeltas.filter(fieldDelta => fieldDelta.newValue || fieldDelta.oldValue).map(fieldDelta => {
      const fieldDef = board?.fields.find(f => f.id === fieldDelta.fieldId)
      const fieldLabel = fieldDef?.label ?? fieldDelta.fieldId
      const oldVal = this.formatFieldValue(fieldDelta.oldValue)
      const newVal = this.formatFieldValue(fieldDelta.newValue)
      return {
        field: 'fields',
        oldValue: fieldDelta.oldValue,
        newValue: fieldDelta.newValue,
        label: `alterou "${fieldLabel}" de "${oldVal}" para "${newVal}"`
      }
    })
  }

  private formatFieldValue(value: unknown): string {
    if (value === null || value === undefined) return 'vazio'
    if (typeof value === 'object' && 'name' in (value as object)) {
      return (value as Kanban.Card.User).name ?? (value as Kanban.Card.User).id ?? 'desconhecido'
    }
    return String(value)
  }
}
